import { createHooks } from "hookable";
import { isString, set } from "lodash";
import { storeToRefs } from "pinia";
import type {
  Cart,
  CartCheckoutInput,
  CartCheckoutResult,
  CartItem,
  CartItemInput,
  CartItemUpdateOptions,
} from "@orie/sdk";
import { useCartStore } from "../stores/cart";
import { salesApi } from "~/services/sales-api";

export const useCart = () => {
  const store = useCartStore();
  const storeRefs = storeToRefs(store);
  const bus = createHooks();
  const sales = salesApi();

  const onError = (cb: { (error: any): void }) => bus.hook("error", cb);

  const load = async (upsert?: boolean) => {
    return new Promise<Cart>((resolve) => {
      store.fetching = true;

      sales.cart
        .get(store.cart.id || "", upsert)
        .then((cart) => {
          store.apply(cart);
          if (cart) {
            resolve(cart);
            store.hydrated = true;
            return store.cart;
          }
        })
        .catch((error) => {
          bus.callHook("error", error);
        })
        .finally(() => {
          store.fetching = false;
        });
    });
  };

  const loadOnce = async (upsert?: boolean) => {
    if (!store.hydrated) {
      await load(upsert);
    }
  };

  const add = async (item: CartItemInput | string) => {
    return new Promise<CartItem>((resolve) => {
      set(store.updating, "add", true);
      const input = isString(item) ? { gid: item } : item;
      sales.cart
        .add(store.cart.id!, input)
        .then((result) => {
          if (result?.cart) {
            store.apply(result.cart);
          }
          resolve(result.item);
        })
        .catch((error) => {
          bus.callHook("error", error);
        })
        .finally(() => {
          set(store.updating, "add", false);
        });
    });
  };

  const update = async (item: string, data: CartItemUpdateOptions) => {
    return new Promise<CartItem>((resolve) => {
      set(store.updating, `update:${item}`, true);
      sales.cart
        .update(store.cart.id!, item, data)
        .then((result) => {
          if (result?.cart) {
            store.apply(result.cart);
          }
          resolve(result.item);
        })
        .catch((error) => {
          bus.callHook("error", error);
        })
        .finally(() => {
          set(store.updating, `update:${item}`, false);
        });
    });
  };

  const remove = async (item: string) => {
    return new Promise<Cart>((resolve) => {
      set(store.updating, `remove:${item}`, true);
      sales.cart
        .remove(store.cart.id!, item)
        .then((result) => {
          if (result) {
            store.apply(result);
          }
          resolve(result);
        })
        .catch((error) => {
          bus.callHook("error", error);
        })
        .finally(() => {
          set(store.updating, `remove:${item}`, false);
        });
    });
  };

  const clear = async () => {
    return new Promise<Cart>((resolve) => {
      set(store.updating, "clear", true);
      sales.cart
        .clear(store.cart.id!)
        .then(() => {
          store.clearData();
          resolve(store.cart);
        })
        .catch((error) => {
          bus.callHook("error", error);
        })
        .finally(() => {
          set(store.updating, "clear", false);
        });
    });
  };

  const isInCart = (gid: string) => {
    const gids = store.items.map((item) => item.gid);
    return gids.includes(gid);
  };

  const checkout = async (data?: CartCheckoutInput) => {
    return new Promise<CartCheckoutResult>((resolve) => {
      store.checkingOut = true;
      sales.cart
        .checkout(store.cart.id!, {
          ...data,
          cancel_url: withoutQuery(window.location.href, "checkout"),
        })
        .then((result) => {
          resolve(result);
        })
        .catch((error) => {
          bus.callHook("error", error);
        })
        .finally(() => {
          store.checkingOut = false;
        });
    });
  };

  const toCheckout = () => {
    const router = useRouter();
    router.push({ name: "cart", query: { checkout: "true" } });
  };

  return {
    ...storeRefs,
    load,
    loadOnce,
    update,
    add,
    remove,
    clear,
    isInCart,
    checkout,
    toCheckout,
    onError,
  };
};
